import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  markActivity,
  isWithinIdleWindow,
  getTokenExpMs,
  REFRESH_BEFORE_MS,
} from "@/contexts/sessionActivity";

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
] as const;
/** How often the guard checks expiry / idle state. */
const CHECK_INTERVAL_MS = 30 * 1000;
/** Coalesce high-frequency events (mousemove/scroll) into one write. */
const ACTIVITY_THROTTLE_MS = 5 * 1000;

/**
 * Keeps an actively-used session alive and logs out only on true inactivity.
 *
 *  - Real user input refreshes a throttled `lastActivity` timestamp.
 *  - A periodic guard silently refreshes the id_token shortly before it expires
 *    while the user is active, and force-logs-out once the idle window
 *    (IDLE_LIMIT_MS) is exceeded.
 *
 * Renders nothing; mounted once near the app root.
 */
export default function SessionManager() {
  const { token, logout, refreshSession } = useAuth();

  // Track real user activity (throttled so mousemove/scroll don't hammer
  // localStorage).
  useEffect(() => {
    if (!token) return;
    // Seed the idle clock so a freshly-loaded, already-authenticated tab isn't
    // treated as instantly idle.
    if (!localStorage.getItem("lastActivity")) markActivity();

    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last < ACTIVITY_THROTTLE_MS) return;
      last = now;
      markActivity();
    };
    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, onActivity, { passive: true })
    );
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [token]);

  // Periodic guard: refresh while active, logout once idle past the cap.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const tick = async () => {
      const current = localStorage.getItem("token");
      if (!current) return;
      // Idle past the cap → end the session regardless of token validity.
      if (!isWithinIdleWindow()) {
        logout();
        return;
      }
      // Active → renew the token before it expires.
      const expMs = getTokenExpMs(current);
      if (expMs && expMs - Date.now() < REFRESH_BEFORE_MS) {
        const ok = await refreshSession();
        if (!ok && !cancelled) logout();
      }
    };

    const id = window.setInterval(tick, CHECK_INTERVAL_MS);
    // Re-check promptly when the tab regains focus (handles wake-from-sleep,
    // where interval timers may have been suspended).
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [token, logout, refreshSession]);

  return null;
}
